import * as React from 'react';
import {
    Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { generateVirtualMatch, getVirtualProviderLeaguesForSport } from '../../Api';
import { VirtualMatchScheduleResultDto, VirtualProviderLeagueDto } from '../../models/dtos/VirtualProviderCatalogDto';
import { VIRTUAL_FOOTBALL_ID, VirtualCatalogSportDto } from '../../models/dtos/VirtualSportsCatalogDto';

const RANDOM_LEAGUE_VALUE = '';

export default function VirtualMatchGenerationDialog(props: {
    open: boolean;
    sports: VirtualCatalogSportDto[];
    onClose: () => void;
    onGenerated: (result: VirtualMatchScheduleResultDto) => void;
}) {
    const [sportId, setSportId] = React.useState<number | ''>('');
    const [leagues, setLeagues] = React.useState<VirtualProviderLeagueDto[]>([]);
    const [loadingLeagues, setLoadingLeagues] = React.useState(false);
    const [leagueId, setLeagueId] = React.useState(RANDOM_LEAGUE_VALUE);
    const [startDate, setStartDate] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const [created, setCreated] = React.useState<VirtualMatchScheduleResultDto | null>(null);

    React.useEffect(() => {
        if (!props.open) return;
        setSportId(props.sports.some(sport => sport.id === VIRTUAL_FOOTBALL_ID) ? VIRTUAL_FOOTBALL_ID : '');
        setLeagues([]);
        setLeagueId(RANDOM_LEAGUE_VALUE);
        setStartDate('');
        setBusy(false);
        setError('');
        setCreated(null);
    }, [props.open, props.sports]);

    React.useEffect(() => {
        if (!props.open || sportId === '') {
            setLeagues([]);
            return;
        }
        let cancelled = false;
        setLoadingLeagues(true);
        setLeagueId(RANDOM_LEAGUE_VALUE);
        getVirtualProviderLeaguesForSport(sportId)
            .then(result => { if (!cancelled) setLeagues(result); })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load leagues'); })
            .finally(() => { if (!cancelled) setLoadingLeagues(false); });
        return () => { cancelled = true; };
    }, [props.open, sportId]);

    const startDateValid = startDate.trim().length === 0 || !Number.isNaN(new Date(startDate).getTime());
    const valid = sportId !== '' && startDateValid;

    const generate = async () => {
        if (sportId === '' || !valid) return;
        setBusy(true);
        setError('');
        try {
            const result = await generateVirtualMatch(sportId, {
                leagueId: leagueId || undefined,
                startDate: startDate ? new Date(startDate).toISOString() : undefined,
            });
            setCreated(result);
            props.onGenerated(result);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not generate the match');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={props.open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="sm">
            <DialogTitle>Generate virtual match</DialogTitle>
            <DialogContent>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                {created ? (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="success">Match generated and scheduled.</Alert>
                        <Typography variant="h6">{created.match.homeTeam.name} vs {created.match.awayTeam.name}</Typography>
                        <Typography color="text.secondary">
                            Kicks off {new Date(created.match.startDate).toLocaleString()}
                        </Typography>
                    </Stack>
                ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <DialogContentText>
                            Picks two random active teams for the sport and schedules the match. Leave the league or start
                            date blank to let the provider choose.
                        </DialogContentText>
                        <TextField
                            select
                            required
                            label="Virtual sport"
                            value={sportId}
                            disabled={busy}
                            onChange={event => setSportId(Number(event.target.value))}
                        >
                            {props.sports.map(sport => <MenuItem key={sport.id} value={sport.id}>{sport.name}</MenuItem>)}
                        </TextField>
                        <TextField
                            select
                            label="League"
                            value={leagueId}
                            disabled={busy || sportId === '' || loadingLeagues}
                            onChange={event => setLeagueId(event.target.value)}
                        >
                            <MenuItem value={RANDOM_LEAGUE_VALUE}>Random league</MenuItem>
                            {leagues.map(league => <MenuItem key={league.id} value={league.id}>{league.name}</MenuItem>)}
                        </TextField>
                        <TextField
                            type="datetime-local"
                            label="Start date (optional)"
                            value={startDate}
                            disabled={busy}
                            onChange={event => setStartDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            helperText="Leave blank to start soon"
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {created ? (
                    <>
                        <Button onClick={props.onClose}>Done</Button>
                        <Button
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => window.open(created.liveUrl, '_blank', 'noopener,noreferrer')}
                        >
                            Live match
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={props.onClose} disabled={busy}>Cancel</Button>
                        <Button variant="contained" disabled={!valid || busy} onClick={generate}>
                            {busy ? <CircularProgress size={20} /> : 'Generate match'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
